/**
 * Profile-cache sync (v1.3.36).
 *
 * Background, debounced upload of the player's per-playlist state to the
 * backend so:
 *   • Admin can audit / wipe a client's saved profiles, favorites,
 *     history and app settings from /admin/profile-cache.
 *   • A re-install can restore the user's state by pulling the snapshot
 *     back on activation.
 *
 * The actual upload is fire-and-forget — never block UX on it and never
 * throw. We collect everything synchronously from localStorage at the
 * moment the debounce timer fires.
 */
import { playerAxios } from "./playerApi";
import { listProfiles } from "./profiles";

const DEBOUNCE_MS = 5000;

/** All localStorage keys that are app-wide (not per-profile). */
const APP_SETTING_KEYS = [
  "vupp_external_player",
  "vupp_engine_live_tv",
  "vupp_engine_movie",
  "vupp_engine_series",
  "vupp_engine_catchup",
  "vupp_builtin_default_v1",
  "vupp_quality_pref",
  "vupp_autoplay_next",
  "vupp_autopip",
  "vupp_subtitle_lang",
  "vupp_audio_lang",
];

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function activePlaylistId() {
  return localStorage.getItem("vupp_active_playlist") || null;
}

function collectSnapshot(playlistId) {
  // Profiles attached to this playlist.
  const profiles = listProfiles().filter((p) => p.playlist_id === playlistId);

  // Favorites + history are stored per-profile under
  // `vupp_favorites_v1__<pid>` and `vupp_watch_history__<pid>`.
  // v1.4.65 — Also collect the watched-items registry under
  // `vupp_watched_items_v1__<pid>` so ✓ marks sync across devices.
  const favorites = {};
  const history = {};
  const watched_items = {};
  for (const p of profiles) {
    favorites[p.id] = readJson(`vupp_favorites_v1__${p.id}`) || {};
    history[p.id] = readJson(`vupp_watch_history__${p.id}`) || [];
    watched_items[p.id] = readJson(`vupp_watched_items_v1__${p.id}`) || [];
  }

  // App-wide settings: a flat object of just the keys we care about.
  const app_settings = {};
  for (const k of APP_SETTING_KEYS) {
    const v = localStorage.getItem(k);
    if (v !== null) app_settings[k] = v;
  }
  // Hidden category lists (keys are dynamic — `vupp_hidden_cat_<playlistId>_<type>`).
  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith("vupp_hidden_cat_")) app_settings[k] = localStorage.getItem(k);
  });

  return {
    profiles: profiles.map((p) => ({
      id: p.id,
      name: p.name,
      avatar_index: p.avatar_index,
      is_kids: !!p.is_kids,
      has_pin: !!p.has_pin,
    })),
    favorites,
    history,
    watched_items,
    app_settings,
  };
}

async function pushNow(playlistId, snapshot) {
  try {
    // playerAxios already attaches X-Device-ID + X-Activation-Key via
    // its request interceptor — using axios directly here would fail
    // auth and silently drop every sync.
    await playerAxios.post(`/player/profile-cache/${encodeURIComponent(playlistId)}`, snapshot, {
      timeout: 8000,
    });
  } catch {
    /* fire-and-forget — never block UX */
  }
}

let _timer = null;

/**
 * Schedule a profile-cache upload after `DEBOUNCE_MS` of quiet. Safe to
 * call from anywhere — multiple rapid calls collapse to a single PUSH.
 */
export function scheduleProfileCacheSync() {
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => {
    _timer = null;
    const pid = activePlaylistId();
    if (!pid) return;
    pushNow(pid, collectSnapshot(pid));
  }, DEBOUNCE_MS);
}

/**
 * Optional immediate flush — used on app pause/blur so we don't lose a
 * pending change if the WebView is torn down before the debounce fires.
 */
export function flushProfileCacheSync() {
  if (_timer) {
    clearTimeout(_timer);
    _timer = null;
  }
  const pid = activePlaylistId();
  if (!pid) return;
  pushNow(pid, collectSnapshot(pid));
}

/**
 * Pull the server's snapshot for the given playlist. Returns
 * `{found, profiles, favorites, history, app_settings}` or `{found:false}`
 * if there's no cache yet. Never throws.
 */
export async function fetchProfileCache(playlistId) {
  try {
    if (!playlistId) return { found: false };
    const r = await playerAxios.get(`/player/profile-cache/${encodeURIComponent(playlistId)}`, {
      timeout: 8000,
    });
    return r.data || { found: false };
  } catch {
    return { found: false };
  }
}

/**
 * Restore the per-profile favorites/history and app settings into
 * localStorage IF the local copy is empty. Does not touch the active
 * playlist's profile records (those already sync via the existing
 * `/player/profiles` endpoint). Returns `true` when at least one key
 * was restored.
 */
export async function maybeRestoreProfileCache(playlistId) {
  try {
    const cache = await fetchProfileCache(playlistId);
    if (!cache?.found) return false;
    let restored = false;
    const fav = cache.favorites || {};
    Object.entries(fav).forEach(([pid, payload]) => {
      const key = `vupp_favorites_v1__${pid}`;
      const existing = localStorage.getItem(key);
      if (!existing || existing === "{}") {
        try {
          localStorage.setItem(key, JSON.stringify(payload || {}));
          restored = true;
        } catch {/* quota */}
      }
    });
    const hist = cache.history || {};
    Object.entries(hist).forEach(([pid, payload]) => {
      const key = `vupp_watch_history__${pid}`;
      const existing = localStorage.getItem(key);
      if (!existing || existing === "[]") {
        try {
          localStorage.setItem(key, JSON.stringify(payload || []));
          restored = true;
        } catch {/* quota */}
      }
    });
    // v1.4.65 — Restore the watched-items registry per profile.
    const watched = cache.watched_items || {};
    Object.entries(watched).forEach(([pid, payload]) => {
      const key = `vupp_watched_items_v1__${pid}`;
      const existing = localStorage.getItem(key);
      if (!existing || existing === "[]") {
        try {
          localStorage.setItem(key, JSON.stringify(payload || []));
          restored = true;
        } catch {/* quota */}
      }
    });
    const settings = cache.app_settings || {};
    Object.entries(settings).forEach(([k, v]) => {
      if (localStorage.getItem(k) == null && typeof v === "string") {
        try { localStorage.setItem(k, v); restored = true; } catch {/* quota */}
      }
    });
    return restored;
  } catch {
    return false;
  }
}
